import { useRouteOrganizationId } from '@/hooks/useRouteOrganizationId'
import queryClient from '@/lib/queryClient'
import {
  buildAdminQueryRoot,
  buildScopedAdminQueryKey,
  type AdminQueryKey,
  type AdminQueryRoot,
  type OrganizationIdInput,
} from '@/lib/organization-scoping'
import { salesOrderQueryKeys } from '@/lib/services/sales-orders/sales-orders.query'
import { queryOptions, useMutation, useQuery, type Query } from '@tanstack/react-query'
import {
  createDeliveryShare,
  getDeliveryRouteLocations,
  getDeliveryShare,
  getDeliveryShareLiveState,
  getDeliveryShares,
  revokeDeliveryShare,
  sendDeliveryShare,
  updateDeliveryShare,
  type DeliveryShareIndexQueryKeyEntries,
  type DeliveryShareScope,
  type DeliveryShareShowKeyEntries,
} from './delivery-shares.api'
import type { DeliveryShareFilters } from './delivery-shares.schema'
import type {
  DeliveryShareIdInput,
  DeliveryShareInput,
  SendDeliveryShareInput,
  UpdateDeliveryShareInput,
} from './delivery-shares.types'
import { invalidateDeliveryRouteContactQueries } from '../delivery-route-contacts/delivery-route-contacts.query'

interface OrganizationScope {
  organizationId?: OrganizationIdInput
}

interface DeliveryShareIndexScope extends OrganizationScope {
  filters?: DeliveryShareFilters | undefined
}

/** The key entries each fetcher reads; the scoped key builders below set them. */
type DeliveryShareIndexQueryKey = [AdminQueryRoot & DeliveryShareIndexQueryKeyEntries]
type DeliveryShareShowQueryKey = [AdminQueryRoot & DeliveryShareShowKeyEntries]

/** Query options a caller may override; the key and fetcher stay owned by this module. */
type QueryParams<TOptions> = Partial<Omit<TOptions, 'queryKey' | 'queryFn'>>

export const deliveryShareQueryKeys = {
  all: ({ organizationId }: OrganizationScope = {}): AdminQueryKey => [
    buildAdminQueryRoot({ entity: 'deliveryShare' }, { organizationId }),
  ],
  index: ({ organizationId, filters }: DeliveryShareIndexScope = {}): DeliveryShareIndexQueryKey =>
    buildScopedAdminQueryKey(deliveryShareQueryKeys.all({ organizationId })[0], {
      organizationId,
      scope: 'index',
      filters,
    }),
  routeLocations: ({ organizationId }: OrganizationScope = {}) =>
    buildScopedAdminQueryKey(deliveryShareQueryKeys.all({ organizationId })[0], {
      organizationId,
      scope: 'routeLocations',
    }),
  show: ({ organizationId, deliveryShareId }: DeliveryShareScope): DeliveryShareShowQueryKey =>
    buildScopedAdminQueryKey(deliveryShareQueryKeys.all({ organizationId })[0], {
      organizationId,
      scope: 'show',
      deliveryShareId: Number(deliveryShareId),
    }),
  liveState: ({ organizationId, deliveryShareId }: DeliveryShareScope): DeliveryShareShowQueryKey =>
    buildScopedAdminQueryKey(deliveryShareQueryKeys.all({ organizationId })[0], {
      organizationId,
      scope: 'liveState',
      deliveryShareId: Number(deliveryShareId),
    }),
}

export const deliveryShareQueries = {
  index: ({ organizationId, filters }: DeliveryShareIndexScope = {}) =>
    queryOptions({
      queryKey: deliveryShareQueryKeys.index({ organizationId, filters }),
      queryFn: getDeliveryShares,
    }),
  routeLocations: ({ organizationId }: OrganizationScope = {}) =>
    queryOptions({
      queryKey: deliveryShareQueryKeys.routeLocations({ organizationId }),
      queryFn: getDeliveryRouteLocations,
    }),
  show: ({ organizationId, deliveryShareId }: DeliveryShareScope) =>
    queryOptions({
      queryKey: deliveryShareQueryKeys.show({ organizationId, deliveryShareId }),
      queryFn: getDeliveryShare,
    }),
  liveState: ({ organizationId, deliveryShareId }: DeliveryShareScope) =>
    queryOptions({
      queryKey: deliveryShareQueryKeys.liveState({ organizationId, deliveryShareId }),
      queryFn: getDeliveryShareLiveState,
    }),
}

function isDeliveryShareIndexQuery(query: Query, organizationId: number) {
  const key: unknown = query.queryKey[0]

  return (
    typeof key === 'object' &&
    key !== null &&
    'entity' in key &&
    key.entity === 'deliveryShare' &&
    'organizationId' in key &&
    key.organizationId === organizationId &&
    'scope' in key &&
    key.scope === 'index'
  )
}

function invalidateDeliveryShareIndexQueries(organizationId: number) {
  return queryClient.invalidateQueries({
    predicate: (query) => isDeliveryShareIndexQuery(query, organizationId),
  })
}

export function useDeliveryShares({
  organizationId,
  filters,
  ...params
}: DeliveryShareIndexScope & QueryParams<ReturnType<typeof deliveryShareQueries.index>> = {}) {
  const resolvedOrganizationId = useRouteOrganizationId(organizationId)

  return useQuery({
    ...deliveryShareQueries.index({ organizationId: resolvedOrganizationId, filters }),
    ...params,
  })
}

export function useDeliveryShare(
  deliveryShareId: DeliveryShareIdInput,
  {
    organizationId,
    ...params
  }: OrganizationScope & QueryParams<ReturnType<typeof deliveryShareQueries.show>> = {}
) {
  const resolvedOrganizationId = useRouteOrganizationId(organizationId)

  return useQuery({
    ...deliveryShareQueries.show({
      organizationId: resolvedOrganizationId,
      deliveryShareId,
    }),
    ...params,
  })
}

export function useCreateDeliveryShareMutation() {
  const organizationId = useRouteOrganizationId()

  return useMutation({
    mutationFn: (values: DeliveryShareInput) => createDeliveryShare({ organizationId, ...values }),
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({
          queryKey: deliveryShareQueryKeys.all({ organizationId }),
        }),
        queryClient.invalidateQueries({
          queryKey: salesOrderQueryKeys.all({ organizationId }),
        }),
      ]),
  })
}

export function useUpdateDeliveryShareMutation() {
  const organizationId = useRouteOrganizationId()

  return useMutation({
    mutationFn: (values: UpdateDeliveryShareInput) =>
      updateDeliveryShare({ organizationId, ...values }),
    onSuccess: (_, variables) =>
      Promise.all([
        invalidateDeliveryShareIndexQueries(organizationId),
        queryClient.invalidateQueries({
          queryKey: deliveryShareQueryKeys.show({
            organizationId,
            deliveryShareId: variables.deliveryShareId,
          }),
        }),
        queryClient.invalidateQueries({
          queryKey: deliveryShareQueryKeys.liveState({
            organizationId,
            deliveryShareId: variables.deliveryShareId,
          }),
        }),
        queryClient.invalidateQueries({
          queryKey: salesOrderQueryKeys.all({ organizationId }),
        }),
      ]),
  })
}

export function useSendDeliveryShareMutation() {
  const organizationId = useRouteOrganizationId()

  return useMutation({
    mutationFn: (values: SendDeliveryShareInput & { deliveryShareId: DeliveryShareIdInput }) =>
      sendDeliveryShare({ organizationId, ...values }),
    onSuccess: (_, variables) =>
      Promise.all([
        queryClient.invalidateQueries({
          queryKey: deliveryShareQueryKeys.all({ organizationId }),
        }),
        queryClient.invalidateQueries({
          queryKey: deliveryShareQueryKeys.show({
            organizationId,
            deliveryShareId: variables.deliveryShareId,
          }),
        }),
        invalidateDeliveryRouteContactQueries(organizationId),
      ]),
  })
}

export function useRevokeDeliveryShareMutation() {
  const organizationId = useRouteOrganizationId()

  return useMutation({
    mutationFn: (deliveryShareId: DeliveryShareIdInput) =>
      revokeDeliveryShare({ organizationId, deliveryShareId }),
    onSuccess: (_, deliveryShareId) => {
      queryClient.removeQueries({
        queryKey: deliveryShareQueryKeys.show({
          organizationId,
          deliveryShareId,
        }),
        exact: true,
      })
      queryClient.removeQueries({
        queryKey: deliveryShareQueryKeys.liveState({
          organizationId,
          deliveryShareId,
        }),
        exact: true,
      })

      return Promise.all([
        invalidateDeliveryShareIndexQueries(organizationId),
        queryClient.invalidateQueries({
          queryKey: salesOrderQueryKeys.all({ organizationId }),
        }),
      ])
    },
  })
}
